(() => {
  const SECTION_SELECTOR = "[data-custom-wishlist-plp]";
  const COMPONENT_SELECTOR = "wishlist-page";

  function getRenderRoot(component) {
    return component.shadowRoot || component;
  }

  function hasText(element) {
    return Boolean(element && element.textContent && element.textContent.trim().length > 0);
  }

  function hasImageSource(image) {
    if (!image) return false;

    if (image.tagName === "IMG") {
      return Boolean(
        image.currentSrc ||
          image.getAttribute("src") ||
          image.getAttribute("srcset")
      );
    }

    return true;
  }

  function isRealProductCard(card) {
    const productCard = card.matches(".wk-product-card")
      ? card
      : card.querySelector(".wk-product-card") || card;

    const image = productCard.querySelector("img, .wk-image");
    const title = productCard.querySelector(".wk-product-title, .wk-text-link");
    const price = productCard.querySelector(".wk-price");
    const cta = productCard.querySelector(".wk-cta-button");

    return Boolean(
      hasImageSource(image) &&
        (hasText(title) || hasText(price) || hasText(cta))
    );
  }

  function getProductCards(root) {
    const cardHosts = Array.from(root.querySelectorAll("wishlist-product-card"));

    if (cardHosts.length) {
      return cardHosts;
    }

    return Array.from(root.querySelectorAll(".wk-product-card"));
  }

  function hasWishlistContent(component) {
    const root = getRenderRoot(component);

    if (!root) return false;

    /*
      Empty/Login States dürfen sofort als fertig gelten,
      weil dort keine Produktkarten mehr nachladen müssen.
    */
    if (
      root.querySelector(
        [
          ".wk-wishlist-empty-callout",
          ".wk-login-callout",
        ].join(",")
      )
    ) {
      return true;
    }

    const productCards = getProductCards(root);

    if (!productCards.length) {
      return false;
    }

    /*
      Wichtig:
      Nicht schon bei .wk-grid, .wk-page oder wishlist-product-card fertig melden.
      Erst wenn alle gefundenen Cards echten Produktinhalt haben.
      Dadurch bleiben eure Skeletons sichtbar, während Swish intern noch Mini-Spinner zeigt.
    */
    return productCards.every(isRealProductCard);
  }

  // Swish renders its own prices asynchronously. Convert the displayed
  // localized amounts to cents, keeping percent calculations consistent
  // with the theme's Liquid floor() logic.
  function priceTextToCents(text) {
    const matched = String(text || "").replace(/\u00a0/g, " ").match(/\d[\d.,\s]*/);
    if (!matched) return null;

    const raw = matched[0].replace(/\s/g, "");
    const lastDot = raw.lastIndexOf(".");
    const lastComma = raw.lastIndexOf(",");
    const separator = Math.max(lastDot, lastComma);
    const decimalCount = separator >= 0 ? raw.length - separator - 1 : 0;

    let amount;
    if (separator >= 0 && decimalCount > 0 && decimalCount <= 2) {
      const major = raw.slice(0, separator).replace(/\D/g, "");
      const minor = raw.slice(separator + 1).replace(/\D/g, "").padEnd(2, "0");
      amount = Number(major) * 100 + Number(minor);
    } else {
      amount = Number(raw.replace(/\D/g, "")) * 100;
    }

    return Number.isFinite(amount) ? amount : null;
  }

  function initWishlistDiscountBadges(component) {
    let scheduled = false;
    const root = getRenderRoot(component);
    if (!root) return;
    const observedRoots = new WeakSet();

    function observePriceRoot(currentRoot) {
      if (!currentRoot || observedRoots.has(currentRoot)) return;
      observer.observe(currentRoot, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      observedRoots.add(currentRoot);
    }

    function updateBadges() {
      scheduled = false;

      const roots = [getRenderRoot(component)];
      roots[0]?.querySelectorAll("wishlist-product-card").forEach((card) => {
        if (card.shadowRoot) roots.push(card.shadowRoot);
      });

      roots.forEach((currentRoot) => {
        if (!currentRoot) return;
        observePriceRoot(currentRoot);
        currentRoot.querySelectorAll(".wk-price").forEach((priceRow) => {
          const current = priceRow.querySelector(".wk-current-price");
          const compare = priceRow.querySelector(".wk-compare-price");
          const currentCents = priceTextToCents(current?.textContent);
          const compareCents = priceTextToCents(compare?.textContent);
          const percent =
            currentCents !== null &&
            compareCents !== null &&
            compareCents > currentCents
              ? Math.floor(((compareCents - currentCents) * 100) / compareCents)
              : 0;

          let badge = priceRow.querySelector(".custom-discount-badge");
          if (percent <= 0) {
            badge?.remove();
            return;
          }

          const label = `-${percent}%`;
          if (!badge) {
            badge = document.createElement("span");
            badge.className = "custom-discount-badge";
            priceRow.appendChild(badge);
          }
          if (badge.textContent !== label) badge.textContent = label;
          badge.setAttribute("aria-label", `${percent} Prozent Rabatt`);
        });
      });
    }

    function scheduleUpdate() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(updateBadges);
    }

    const observer = new MutationObserver(scheduleUpdate);
    observePriceRoot(root);

    // Swish can replace the component's content after its custom element loads.
    if (window.customElements) {
      customElements.whenDefined("wishlist-page").then(scheduleUpdate).catch(() => {});
    }
    scheduleUpdate();
  }

  function initWishlistPlp(section) {
    const component = section.querySelector(COMPONENT_SELECTOR);

    if (!component) return;

    initWishlistDiscountBadges(component);

    let loaded = false;
    let observedRoot = null;

    const observer = new MutationObserver(() => {
      observeCurrentRoot();
      markLoaded();
    });

    function observeCurrentRoot() {
      const root = getRenderRoot(component);

      if (!root || root === observedRoot) return;

      observer.disconnect();

      observer.observe(root, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["src", "srcset", "class", "hidden"],
      });

      observedRoot = root;
    }

    function markLoaded() {
      if (loaded) return;

      observeCurrentRoot();

      if (!hasWishlistContent(component)) return;

      loaded = true;

      section.classList.remove("is-loading", "has-error", "is-waiting");
      section.classList.add("is-loaded");

      observer.disconnect();
    }

    section.classList.add("is-loading");

    observeCurrentRoot();

    if (window.customElements) {
      customElements
        .whenDefined("wishlist-page")
        .then(() => {
          section.classList.add("is-component-defined");
          observeCurrentRoot();
          markLoaded();
        })
        .catch(() => {});
    }

    const interval = window.setInterval(() => {
      markLoaded();

      if (loaded) {
        window.clearInterval(interval);
      }
    }, 150);

    window.setTimeout(() => {
      if (!loaded) {
        section.classList.add("is-waiting");
      }
    }, 2000);

    window.setTimeout(() => {
      if (!loaded) {
        window.clearInterval(interval);
        observer.disconnect();

        section.classList.remove("is-loading", "is-waiting");
        section.classList.add("has-error");
      }
    }, 12000);
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(initWishlistPlp);
})();